
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Observable, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AppUserProfile } from '../../../core-module/models/user-info';
import { AccountService } from '../../../core-module/services/account.service';
import { UserProfileService } from '../../../core-module/services/user-profile.service';
import { AppAccessToken } from '../../../shared-module/models/app-access-token';
import { PersonNamePipe } from '../../../shared-module/pipes/person-name.pipe';
import { AuthService } from '../../../shared-module/services/auth.service';
import { JwtTokenService } from '../../../shared-module/services/jwt-token.service';
import { LocalStorageService } from '../../../shared-module/services/local-storage.service';
import { RoutingService } from '../../../shared-module/services/routing.service';
import { Constants } from '../../../shared-module/utils/constants';
import { toSignal } from '@angular/core/rxjs-interop';
import { ObservableBaseComponent } from '../../../shared-module/components/observable-base.component';
import { filterManagementFeatures } from 'app/components/managements/management-feature.utils';
import { AuthStore } from '@scl/core/domains/auth/services/auth.store';

@Component({
  selector: 'app-profile-nav-menu',
  imports: [
    PersonNamePipe,
    RouterModule
],
  templateUrl: './profile-nav-menu.component.html',
  styleUrls: ['./profile-nav-menu.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileNavMenuComponent extends ObservableBaseComponent {

  private authService = inject(AuthService);
  private authStore = inject(AuthStore);
  private jwtTokenService = inject(JwtTokenService);
  private routingService = inject(RoutingService);
  private userProfileService = inject(UserProfileService);
  private localStorageService = inject(LocalStorageService);
  accountService = inject(AccountService);

  switchProfile$!: Observable<AppUserProfile> | null;
  switchToRole = signal<string | null>(null);
  hasAuthDataMain = signal(false);

  private currentUser = toSignal(this.accountService.currentUser$, { initialValue: null });
  private userRoles = computed(() => this.currentUser()?.roles?.map((r: any) => r.name) ?? []);
  private userPermissions = computed(() => this.currentUser()?.permissions ?? []);

  user = toSignal(this.accountService.currentUser$.pipe(
    takeUntil(this.unsubscribe$),
    switchMap(usr => {
      const authDataMain = this.localStorageService.getItem(Constants.AuthDataMain);
      this.switchToRole.set(usr?.userProfile?.switchableProfileName || null);
      this.hasAuthDataMain.set(!!authDataMain);
      return of(usr);
    })
  ));

  showReturnToMain = computed(() =>
    this.hasAuthDataMain() || this.authStore.isImpersonating() || this.authStore.hasReturnTicket()
  );

  logout() {
    this.accountService.currentUser = null;
    this.userProfileService.removeUserProfile();

    if (this.authStore.isClerk()) {
      localStorage.removeItem(ProfileNavMenuComponent.ACTIVE_PROFILE_KEY);
      this.authService.clearToken();
      void this.authStore.logout();
      return;
    }

    this.authService.clearTokenAndEndSession();
  }

  showManagements() {
    return filterManagementFeatures(
          this.userRoles(),
          this.userPermissions()
        ).length > 0;
  }

  private static readonly ACTIVE_PROFILE_KEY = 'scl_active_profile';
  private static readonly MANAGEMENT_ROLES = new Set([
    'Administrator', 'Secondary Admin', 'Branch Manager'
  ]);

  private cleanupAndLogout() {
    this.accountService.currentUser = null;
    this.userProfileService.removeUserProfile();
    localStorage.removeItem(ProfileNavMenuComponent.ACTIVE_PROFILE_KEY);
    void this.authStore.logout();
  }

  onSwitchProfile() {
    if(this.switchToRole() === null) {
      return;
    }

    if (this.authStore.isClerk()) {
      this.onSwitchProfileClerk(this.switchToRole()!);
      return;
    }

    // Notes: needs switchProfile$ because userInfo$ is tap into accessToken$
    // and when doing onImpersonate, only accessToken has been changed.
    this.userProfileService.switchProfile(this.switchToRole()!)
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(token => {
          this.jwtTokenService.accessToken = token!;
          return of(token);
        }),
        switchMap(tk => this.accountService.getUserInfo()),
        switchMap(usr => {
          this.routingService.navigateAuthUser(true);
          return of(usr);
        })
      ).subscribe();
  }

  private onSwitchProfileClerk(profile: string) {
    if (ProfileNavMenuComponent.MANAGEMENT_ROLES.has(profile)) {
      localStorage.removeItem(ProfileNavMenuComponent.ACTIVE_PROFILE_KEY);
    } else {
      localStorage.setItem(ProfileNavMenuComponent.ACTIVE_PROFILE_KEY, profile);
    }
    this.accountService.getUserInfo()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.routingService.navigateAuthUser(true));
  }

  returnToMainAccount() {
    if (this.authStore.isImpersonating() || this.authStore.hasReturnTicket()) {
      void this.authStore.returnFromImpersonation().then(restored => {
        if (restored) {
          this.accountService.getUserInfo()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
              next: () => this.routingService.navigateAuthUser(true),
              error: () => this.cleanupAndLogout()
            });
        } else {
          this.cleanupAndLogout();
        }
      }).catch(() => this.cleanupAndLogout());
      return;
    }

    const authDataMain = this.localStorageService.getItem<AppAccessToken>(Constants.AuthDataMain);
    this.jwtTokenService.accessToken = authDataMain;
    this.accountService.getUserInfo()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        // Remove only after getUserInfo succeeds — preserves recovery if the token is expired
        this.localStorageService.removeItem(Constants.AuthDataMain);
        this.routingService.navigateAuthUser(true);
      });
  }
}
